import { useStore } from '@/engine/store';
import { Env } from '@/env';
import { router } from '@/main';
import { type ApiClient, type ApiClientConfig, type BaseApi, type MonteursGameApi } from '@shared/modelTypes/api-types';
import { redirect } from '@tanstack/react-router';

function makeApiClient<TApi extends BaseApi>(config: ApiClientConfig<TApi>) {
	const target = {};
	return new Proxy(target, {
		get(target, props, receiver) {
			console.log('proxy called', target, props, receiver);
			return (params: any) => {
				return config.handler({ endpoint: props as any, params });
			};
		},
	}) as ApiClient<TApi>;
}

export const api = makeApiClient<MonteursGameApi>({
	handler: async (opts) => {
		try {
			const res = (await fetch(Env.VITE_API_URL, {
				method: 'post',
				headers: {
					'Content-Type': 'application/json',
					accept: 'application/json',
				},
				body: JSON.stringify(opts),
			}).then((res) => res.json())) as {
				success: boolean;
				data: any;
				message: string;
			};

			if (res.success) {
				return res.data;
			} else {
				console.error('api call fail:', res);
				throw new Error(res.message);
			}
		} catch (e) {
			console.error('api call fail:', e);
			throw new Error('API call failed');
		}
	},
});

export function getRedirectUrl(location?: string): string | null {
	if (!location) location = window.location.pathname;

	// observing the game is always allowed
	if (location === '/observe-game') {
		return null;
	}

	const currentState = useStore.getState();
	console.log('currentState', currentState);

	// initially no location is set so you will be redirected to /set-location
	// after filling in your location code for a valid location state.location should be set
	if (!currentState.location) return '/set-location';

	// if location is set but no session is active we will redirect to the no-game page
	// this should only happen in the case where a new location is created but a game was not started yet
	// the no-game screen will have a button to manually try and join the game again once it is started
	if (currentState.location && !currentState.gameState) return '/no-game';

	if (currentState.location && currentState.gameState && currentState.gameState.status === 'closed') return '/game-ended';

	// if you are in a game but you have not set your player state you must do that first before playing the game
	// TODO: make it so an admin could observe the game instead of being redirected here
	if (currentState.location && currentState.gameState && !currentState.playerState) return '/set-player';

	// finally: all state to participate in a game is found!
	// go to the /game page
	if (currentState.gameState && currentState.playerState && currentState.location) return '/game';

	return null;
}

export function navigateToDesiredUrl() {
	const desiredUrl = getRedirectUrl();
	if (desiredUrl) {
		// console.log('navigating to desired url: ', location.pathname, desiredUrl);
		router.navigate({ to: desiredUrl });
	}
}

export function redirectBasedOnState({ location }: { location: any }) {
	const redirectUrl = getRedirectUrl(location.pathname);

	if (redirectUrl && redirectUrl !== location.pathname) {
		console.trace('redirecting to', redirectUrl, 'from', location.pathname);
		throw redirect({ to: redirectUrl });
	}
}
